/**
 * 我的任务页面（新版）- 左右双容器布局
 * 左边容器：学习任务（未学习/已学习-已过期）
 * 右边容器：考试任务（未考试/已考试-已过期）
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Clock,
  Calendar,
  CheckCircle,
  BookOpen,
  FileText,
  Play,
  Trophy,
  Target,
  Loader2,
  X,
  AlertCircle,
  File,
  FileImage,
  FileVideo,
  FileAudio,
  FileSpreadsheet,
  Presentation,
  Eye,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { StudentExamTask, StudentLearningTask, LearningTaskDetail, LearningFileProgress } from '../types/api';
import ExamPage from '../components/ExamPage';
import ExamResultPage from '../components/ExamResultPage';
import PdfViewer from '../components/PdfViewer';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import styles from './MyTasks.module.css';

// Base64 编码函数（用于 KKFileView URL）
const base64Encode = (str: string): string => {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch {
    return btoa(str);
  }
};

// 构造 KKFileView 内部下载 URL 的基础地址，强制使用 HTTP
// 避免 KKFileView（Java 8）在 HTTPS 自签名证书环境下因 SSL 验证失败（PKIX）导致文件下载为空
const getKKFileViewBaseUrl = (): string => {
  return `http://${window.location.host}`;
};

// 文件类型图标映射
const getFileIcon = (fileType: string) => {
  const type = fileType.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(type)) {
    return <FileImage size={20} />;
  }
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(type)) {
    return <FileVideo size={20} />;
  }
  if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(type)) {
    return <FileAudio size={20} />;
  }
  if (['pdf'].includes(type)) {
    return <FileText size={20} />;
  }
  if (['doc', 'docx'].includes(type)) {
    return <FileText size={20} />;
  }
  if (['xls', 'xlsx'].includes(type)) {
    return <FileSpreadsheet size={20} />;
  }
  if (['ppt', 'pptx'].includes(type)) {
    return <Presentation size={20} />;
  }
  return <File size={20} />;
};

// 格式化学习时长
const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}秒`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${minutes}分${secs}秒` : `${minutes}分钟`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}小时${minutes}分` : `${hours}小时`;
  }
};

// 格式化文件大小
const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// 格式化日期时间
const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// 简化的日期时间格式，用于截止时间显示
const formatShortDateTime = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);

  // 获取月、日、小时和分钟
  const month = date.getMonth() + 1; // getMonth() 返回 0-11，需要加 1
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${month}月${day}日 ${hours}:${minutes}`;
};

// 距到期时间是否小于 24 小时（尚未过期）
const isUrgent = (deadlineStr: string | null | undefined): boolean => {
  if (!deadlineStr) return false;
  const deadline = new Date(deadlineStr).getTime();
  const now = Date.now();
  const diff = deadline - now;
  return diff > 0 && diff < 24 * 60 * 60 * 1000;
};

// 格式化为"剩余 X 小时 / X 小时 X 分"
const formatUrgentRemain = (deadlineStr: string | null | undefined): string => {
  if (!deadlineStr) return '';
  const deadline = new Date(deadlineStr).getTime();
  const diff = deadline - Date.now();
  if (diff <= 0) return '已截止';
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  if (hours < 1) return `剩余 ${minutes} 分钟`;
  return `剩余 ${hours} 小时 ${minutes} 分`;
};

export default function MyTasksNewPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();

  // 数据状态
  const [examTasks, setExamTasks] = useState<StudentExamTask[]>([]);
  const [learningTasks, setLearningTasks] = useState<StudentLearningTask[]>([]);
  const [loading, setLoading] = useState(true);

  // 标签页状态 - 左右容器各自独立
  const [learningTab, setLearningTab] = useState<'incomplete' | 'completed'>('incomplete');
  const [examTab, setExamTab] = useState<'incomplete' | 'completed'>('incomplete');

  // 弹窗状态
  const [selectedTask, setSelectedTask] = useState<StudentExamTask | StudentLearningTask | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // 学习详情状态
  const [learningDetail, setLearningDetail] = useState<LearningTaskDetail | null>(null);
  const [learningDetailLoading, setLearningDetailLoading] = useState(false);
  const [isLearningModalOpen, setIsLearningModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<LearningFileProgress | null>(null);
  const [fileLearnTimers, setFileLearnTimers] = useState<Map<number, number>>(new Map());

  // 视频预览状态
  const [videoDuration, setVideoDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 考试页面状态
  const [examTask, setExamTask] = useState<StudentExamTask | null>(null);
  const [isExamPageOpen, setIsExamPageOpen] = useState(false);

  // 考试开始确认弹窗状态
  const [examConfirmTask, setExamConfirmTask] = useState<StudentExamTask | null>(null);
  const [isExamConfirmOpen, setIsExamConfirmOpen] = useState(false);

  // 考试结果页面状态
  const [resultTaskId, setResultTaskId] = useState<number | null>(null);
  const [isExamResultOpen, setIsExamResultOpen] = useState(false);

  // 加载任务数据
  useEffect(() => {
    if (user?.user_id) {
      loadTasks();
    }
  }, [user?.user_id]);

  // 清理学习计时器
  useEffect(() => {
    return () => {
      Object.keys(window).forEach(key => {
        if (key.startsWith('learn_timer_')) {
          clearInterval(window[key]);
        }
      });
    };
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const [examRes, learningRes] = await Promise.all([
        api.getMyExamTasks({ student_id: user!.user_id, page: 1, page_size: 100 }),
        api.getMyLearningTasks({ student_id: user!.user_id, page: 1, page_size: 100 }),
      ]);

      if (examRes.code === 200 && examRes.data) {
        setExamTasks(examRes.data);
      }
      if (learningRes.code === 200 && learningRes.data) {
        setLearningTasks(learningRes.data);
      }
      // 任务状态变化后同步侧边栏"我的任务"红标
      window.dispatchEvent(new Event('refresh-sidebar-counts'));
    } catch (error) {
      console.error('加载任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 学习任务分类
  const incompleteLearningTasks = learningTasks.filter(t =>
    t.status === 'pending' || t.status === 'ongoing'
  );
  const completedLearningTasks = learningTasks.filter(t =>
    t.status === 'completed' || t.status === 'expired'
  );

  // 考试任务分类
  const incompleteExamTasks = examTasks.filter(t =>
    t.status === 'pending' || t.status === 'ongoing'
  );
  const completedExamTasks = examTasks.filter(t =>
    t.status === 'graded' || t.status === 'ended'
  );

  // 排序：临期任务（距到期不足24小时）置顶，其余按截止时间升序
  // 仅"未完成"列表启用置顶，"已完成/已过期"列表保持普通时间排序
  const sortWithUrgentFirst = <T,>(
    list: T[],
    getDeadline: (t: T) => string | null | undefined
  ): T[] => {
    return [...list].sort((a, b) => {
      const aUrgent = isUrgent(getDeadline(a));
      const bUrgent = isUrgent(getDeadline(b));
      if (aUrgent !== bUrgent) return aUrgent ? -1 : 1;
      return new Date(getDeadline(a)!).getTime() - new Date(getDeadline(b)!).getTime();
    });
  };

  // 根据标签页显示对应列表，并按截止时间升序排序（临期置顶）
  const displayLearningTasks = sortWithUrgentFirst(
    learningTab === 'incomplete' ? incompleteLearningTasks : completedLearningTasks,
    (t) => t.deadline
  );

  const displayExamTasks = sortWithUrgentFirst(
    examTab === 'incomplete' ? incompleteExamTasks : completedExamTasks,
    (t) => t.exam_end_time
  );

  // 状态配置
  const getLearningStatusConfig = (status: string) => {
    switch (status) {
      case 'ongoing':
        return { label: '学习中', className: styles.ongoing };
      case 'pending':
        return { label: '待开始', className: styles.pending };
      case 'completed':
        return { label: '已完成', className: styles.completed };
      case 'expired':
        return { label: '已过期', className: styles.expired };
      default:
        return { label: '未知', className: '' };
    }
  };

  const getExamStatusConfig = (status: string) => {
    switch (status) {
      case 'ongoing':
        return { label: '进行中', className: styles.ongoing };
      case 'pending':
        return { label: '待开始', className: styles.pending };
      case 'graded':
        return { label: '已出分', className: styles.graded };
      case 'ended':
        return { label: '已结束', className: styles.ended };
      default:
        return { label: '未知', className: '' };
    }
  };

  // 题目类型名称映射
  const getQuestionTypeName = (type: string): string => {
    const typeMap: Record<string, string> = {
      '单选题': '单选题',
      '多选题': '多选题',
      '判断题': '判断题',
      '填空题': '填空题',
      '简答题': '简答题',
      '论述题': '论述题',
    };
    return typeMap[type] || type;
  };

  // 打开详情弹窗
  const openDetailModal = (task: StudentExamTask | StudentLearningTask, type: 'exam' | 'learning') => {
    setSelectedTask({ ...task, type });
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTask(null);
  };

  // 打开学习详情
  const openLearningModal = async (task: StudentLearningTask) => {
    setIsDetailModalOpen(false);
    setLearningDetailLoading(true);
    setIsLearningModalOpen(true);

    try {
      const res = await api.getLearningTaskDetail(task.learning_task_id, user!.user_id);
      if (res.code === 200 && res.data) {
        setLearningDetail(res.data);
      }
    } catch (error) {
      console.error('加载学习详情失败:', error);
    } finally {
      setLearningDetailLoading(false);
    }
  };

  const closeLearningModal = () => {
    setIsLearningModalOpen(false);
    setLearningDetail(null);
    setPreviewFile(null);
    fileLearnTimers.forEach((_, fileId) => {
      const timer = window[`learn_timer_${fileId}`];
      if (timer) clearInterval(timer);
    });
    setFileLearnTimers(new Map());
    loadTasks();
  };

  // 开始学习文件
  const startLearningFile = (file: LearningFileProgress) => {
    setPreviewFile(file);
    setVideoDuration(0);

    // 开始计时（累计学习时长）
    let elapsed = file.learn_duration;
    const timer = setInterval(() => {
      elapsed += 1;
      setFileLearnTimers(prev => new Map(prev).set(file.file_id, elapsed));
    }, 1000);

    window[`learn_timer_${file.file_id}`] = timer;
  };

  // 视频时长变化处理
  const handleVideoLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    setVideoDuration(Math.round(video.duration));
  };

  // 关闭预览并保存进度
  const closePreview = async () => {
    if (!previewFile || !learningDetail) return;

    // 获取本次学习时长
    const currentElapsed = (fileLearnTimers.get(previewFile.file_id) || previewFile.learn_duration) - previewFile.learn_duration;
    const totalLearnDuration = previewFile.learn_duration + currentElapsed;

    const fileType = previewFile.file_type.toLowerCase();
    const requiredSeconds = previewFile.required_duration || 60;
    let isCompleted = false;

    if (fileType === 'pdf') {
      isCompleted = totalLearnDuration >= requiredSeconds;
    } else if (['mp4', 'webm', 'ogg', 'video'].includes(fileType)) {
      const videoRequiredTime = Math.max(videoDuration, requiredSeconds);
      isCompleted = totalLearnDuration >= videoRequiredTime;
    } else {
      isCompleted = totalLearnDuration >= requiredSeconds;
    }

    // 停止计时器
    const timer = window[`learn_timer_${previewFile.file_id}`];
    if (timer) clearInterval(timer);
    setFileLearnTimers(prev => {
      const newMap = new Map(prev);
      newMap.delete(previewFile.file_id);
      return newMap;
    });

    // 先关闭窗口
    const savedFile = previewFile;
    const savedElapsed = currentElapsed;
    setPreviewFile(null);

    // 后台保存进度
    if (savedElapsed > 0) {
      showToast('info', '已完成学习，正在统计学习时长...');
      try {
        await api.updateFileLearningProgress(
          learningDetail.learning_task_id,
          savedFile.file_id,
          user!.user_id,
          { learn_duration: totalLearnDuration, is_completed: isCompleted }
        );
        setLearningDetail(prev => {
          if (!prev) return prev;
          const newFiles = prev.files.map(f =>
            f.file_id === savedFile.file_id
              ? { ...f, learn_duration: totalLearnDuration, is_completed: isCompleted }
              : f
          );
          const completedCount = newFiles.filter(f => f.is_completed).length;
          return { ...prev, files: newFiles, progress: Math.min(100, Math.round((completedCount / newFiles.length) * 100)), total_learn_duration: newFiles.reduce((sum, f) => sum + f.learn_duration, 0) };
        });
        if (isCompleted) showToast('success', '学习进度已保存，该文件已完成');
        else showToast('success', `学习时长已统计：${formatDuration(totalLearnDuration)}`);
      } catch (error) {
        console.error('保存学习进度失败:', error);
        showToast('error', '学习时长保存失败，请重试');
      }
    }
  };

  // 进入任务
  const handleEnterTask = async (task: StudentExamTask | StudentLearningTask, type: 'exam' | 'learning') => {
    if (type === 'learning') {
      openLearningModal(task as StudentLearningTask);
    } else {
      const examTask = task as StudentExamTask;
      const now = new Date();
      const startTime = new Date(examTask.exam_start_time);
      const endTime = new Date(examTask.exam_end_time);

      // 检查考试是否已开始
      if (now < startTime) {
        showToast('warning', `考试尚未开始，开始时间为 ${formatShortDateTime(examTask.exam_start_time)}`);
        return;
      }

      // 检查考试是否已结束
      if (now > endTime) {
        showToast('error', '考试已结束');
        return;
      }

      // 打开考试开始确认弹窗
      setExamConfirmTask(examTask);
      setIsExamConfirmOpen(true);
    }
  };

  // 确认开始考试
  const confirmStartExam = () => {
    if (examConfirmTask) {
      setExamTask(examConfirmTask);
      setIsExamPageOpen(true);
      setIsExamConfirmOpen(false);
      setExamConfirmTask(null);
    }
  };

  // 取消开始考试
  const cancelStartExam = () => {
    setIsExamConfirmOpen(false);
    setExamConfirmTask(null);
  };

  // 关闭考试页面
  const closeExamPage = () => {
    setIsExamPageOpen(false);
    setExamTask(null);
    loadTasks();
  };

  // 打开考试结果页面
  const openExamResult = (taskId: number) => {
    setIsDetailModalOpen(false);
    setResultTaskId(taskId);
    setIsExamResultOpen(true);
  };

  // 关闭考试结果页面
  const closeExamResult = () => {
    setIsExamResultOpen(false);
    setResultTaskId(null);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Loader2 size={40} className={styles.spinner} />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          加载中...
        </motion.p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 页头 */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>我的任务</h1>
          <p className={styles.subtitle}>管理您的学习任务与考试安排</p>
        </div>
      </div>

      {/* 双栏布局 */}
      <div className={styles.splitContainer}>
        {/* 左边容器：学习任务 */}
        <div className={styles.taskPanel}>
          <div className={styles.panelHeader}>
            <div className={`${styles.panelIcon} ${styles.learningIcon}`}>
              <BookOpen size={20} />
            </div>
            <h2 className={styles.panelTitle}>学习任务</h2>
          </div>

          {/* 标签页 */}
          <div className={styles.panelTabs}>
            <button
              className={`${styles.panelTab} ${learningTab === 'incomplete' ? styles.active : ''}`}
              onClick={() => setLearningTab('incomplete')}
              type="button"
            >
              <span>未学习</span>
              <span className={styles.tabBadge}>{incompleteLearningTasks.length}</span>
            </button>
            <button
              className={`${styles.panelTab} ${learningTab === 'completed' ? styles.active : ''}`}
              onClick={() => setLearningTab('completed')}
              type="button"
            >
              <span>已学习/已过期</span>
              <span className={styles.tabBadge}>{completedLearningTasks.length}</span>
            </button>
          </div>

          {/* 任务列表 */}
          <div className={styles.taskListContainer}>
            {displayLearningTasks.length === 0 ? (
              <div className={styles.emptyState}>
                <BookOpen size={32} />
                <p>{learningTab === 'incomplete' ? '暂无待学习任务' : '暂无已学习/已过期任务'}</p>
              </div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.taskTable}>
                  <thead>
                    <tr>
                      <th>名称</th>
                      <th className={styles.centerCell}>截止时间</th>
                      <th className={styles.centerCell}>进度</th>
                      <th className={styles.centerCell}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayLearningTasks.map((task) => {
                      const statusConfig = getLearningStatusConfig(task.status);
                      const isIncomplete = task.status === 'pending' || task.status === 'ongoing';
                      const urgent = isUrgent(task.deadline) && isIncomplete;

                      return (
                        <tr key={task.learning_task_id} className={urgent ? styles.urgentRow : undefined}>
                          <td>
                            <div className={styles.taskNameCell}>
                              <span>{task.learning_task_name}</span>
                              {urgent && (
                                <span className={styles.urgentBadge}>
                                  ⏰ 即将到期 · {formatUrgentRemain(task.deadline)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={styles.centerCell}>{formatShortDateTime(task.deadline)}</td>
                          <td className={styles.centerCell}>{task.progress || 0}%</td>
                          <td className={styles.centerCell}>
                            <button
                              className={styles.actionBtn}
                              type="button"
                              onClick={() => isIncomplete ? handleEnterTask(task, 'learning') : openDetailModal(task, 'learning')}
                            >
                              {isIncomplete ? '开始学习' : '查看详情'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* 右边容器：考试任务 */}
        <div className={styles.taskPanel}>
          <div className={styles.panelHeader}>
            <div className={`${styles.panelIcon} ${styles.examIcon}`}>
              <FileText size={20} />
            </div>
            <h2 className={styles.panelTitle}>考试任务</h2>
          </div>

          {/* 标签页 */}
          <div className={styles.panelTabs}>
            <button
              className={`${styles.panelTab} ${examTab === 'incomplete' ? styles.active : ''}`}
              onClick={() => setExamTab('incomplete')}
              type="button"
            >
              <span>未考试</span>
              <span className={styles.tabBadge}>{incompleteExamTasks.length}</span>
            </button>
            <button
              className={`${styles.panelTab} ${examTab === 'completed' ? styles.active : ''}`}
              onClick={() => setExamTab('completed')}
              type="button"
            >
              <span>已考试/已过期</span>
              <span className={styles.tabBadge}>{completedExamTasks.length}</span>
            </button>
          </div>

          {/* 任务列表 */}
          <div className={styles.taskListContainer}>
            {displayExamTasks.length === 0 ? (
              <div className={styles.emptyState}>
                <FileText size={32} />
                <p>{examTab === 'incomplete' ? '暂无待考试任务' : '暂无已考试/已过期任务'}</p>
              </div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.taskTable}>
                  <thead>
                    <tr>
                      <th>名称</th>
                      <th className={styles.centerCell}>截止时间</th>
                      <th className={styles.centerCell}>状态</th>
                      <th className={styles.centerCell}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayExamTasks.map((task) => {
                      const statusConfig = getExamStatusConfig(task.status);
                      const isIncomplete = task.status === 'pending' || task.status === 'ongoing';
                      const urgent = isUrgent(task.exam_end_time) && isIncomplete;

                      return (
                        <tr key={task.exam_task_id} className={urgent ? styles.urgentRow : undefined}>
                          <td>
                            <div className={styles.taskNameCell}>
                              <span>{task.task_name}</span>
                              {urgent && (
                                <span className={styles.urgentBadge}>
                                  ⏰ 即将到期 · {formatUrgentRemain(task.exam_end_time)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={styles.centerCell}>{formatShortDateTime(task.exam_end_time)}</td>
                          <td className={styles.centerCell}>
                            <span className={`${styles.statusTag} ${statusConfig.className}`}>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className={styles.centerCell}>
                            <button
                              className={styles.actionBtn}
                              type="button"
                              onClick={() => {
                                if (task.status === 'ongoing' || task.status === 'pending') {
                                  handleEnterTask(task, 'exam');
                                } else {
                                  openDetailModal(task, 'exam');
                                }
                              }}
                            >
                              {task.status === 'ongoing' || task.status === 'pending' ? (
                                '开始考试'
                              ) : (
                                '查看详情'
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 详情弹窗 */}
      {isDetailModalOpen && selectedTask && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={closeDetailModal}
        >
          <motion.div
            className={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>任务详情</h2>
              <motion.button
                className={styles.modalClose}
                whileHover={{ rotate: 90 }}
                onClick={closeDetailModal}
              >
                <X size={20} />
              </motion.button>
            </div>
            <div className={styles.modalBody}>
              {'exam_task_id' in selectedTask ? (
                // 考试任务详情
                <>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>任务名称</span>
                    <span className={styles.detailValue}>{(selectedTask as StudentExamTask).task_name}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>考试时间</span>
                    <span className={styles.detailValue}>
                      {formatShortDateTime((selectedTask as StudentExamTask).exam_start_time)} - {formatShortDateTime((selectedTask as StudentExamTask).exam_end_time)}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>及格/满分</span>
                    <span className={styles.detailValue}>{(selectedTask as StudentExamTask).pass_score} 分 / {(selectedTask as StudentExamTask).total_score} 分</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>分配时间</span>
                    <span className={styles.detailValue}>{formatShortDateTime(selectedTask.assign_time)}</span>
                  </div>
                  {/* 已过期未完成 */}
                  {selectedTask.status === 'ended' && !(selectedTask as StudentExamTask).complete_time && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>状态</span>
                      <span className={`${styles.detailValue} ${styles.expiredText}`}>
                        <AlertCircle size={16} />
                        已过期未完成
                      </span>
                    </div>
                  )}
                  {/* 已完成考试 */}
                  {(selectedTask.status === 'graded' || (selectedTask as StudentExamTask).complete_time) && (
                    <>
                      {(selectedTask as StudentExamTask).complete_time && (
                        <div className={styles.detailItem}>
                          <span className={styles.detailLabel}>完成时间</span>
                          <span className={styles.detailValue}>{formatShortDateTime((selectedTask as StudentExamTask).complete_time!)}</span>
                        </div>
                      )}
                      {(selectedTask as StudentExamTask).score !== null && (
                        <div className={styles.detailItem}>
                          <span className={styles.detailLabel}>分数</span>
                          <span className={`${styles.detailValue} ${(selectedTask as StudentExamTask).score! >= (selectedTask as StudentExamTask).pass_score ? styles.passedScore : styles.failedScore}`}>
                            {(selectedTask as StudentExamTask).score} 分
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                // 学习任务详情
                <>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>任务名称</span>
                    <span className={styles.detailValue}>{(selectedTask as StudentLearningTask).learning_task_name}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>分配时间</span>
                    <span className={styles.detailValue}>{formatShortDateTime(selectedTask.assign_time)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>截止时间</span>
                    <span className={styles.detailValue}>{formatShortDateTime((selectedTask as StudentLearningTask).deadline)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>学习进度</span>
                    <span className={styles.detailValue}>{(selectedTask as StudentLearningTask).progress}%</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>已学习时长</span>
                    <span className={styles.detailValue}>{(selectedTask as StudentLearningTask).learn_duration ? formatDuration((selectedTask as StudentLearningTask).learn_duration!) : '0分钟'}</span>
                  </div>
                  {/* 已过期未完成 */}
                  {selectedTask.status === 'expired' && !(selectedTask as StudentLearningTask).complete_time && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>状态</span>
                      <span className={`${styles.detailValue} ${styles.expiredText}`}>
                        <AlertCircle size={16} />
                        已过期未完成
                      </span>
                    </div>
                  )}
                  {/* 已完成学习 */}
                  {(selectedTask.status === 'completed' || (selectedTask as StudentLearningTask).complete_time) && (selectedTask as StudentLearningTask).complete_time && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>完成时间</span>
                      <span className={styles.detailValue}>{formatShortDateTime((selectedTask as StudentLearningTask).complete_time!)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              <motion.button
                className={styles.closeBtn}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={closeDetailModal}
              >
                关闭
              </motion.button>
              {!('exam_task_id' in selectedTask) && (
                <motion.button
                  className={styles.enterBtn}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => openLearningModal(selectedTask as StudentLearningTask)}
                >
                  开始学习
                </motion.button>
              )}
              {'exam_task_id' in selectedTask && selectedTask.status === 'graded' && (
                <motion.button
                  className={styles.enterBtn}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => openExamResult((selectedTask as StudentExamTask).exam_task_id)}
                >
                  查看答案解析
                </motion.button>
              )}
              {'exam_task_id' in selectedTask && selectedTask.status === 'ongoing' && (
                <motion.button
                  className={styles.enterBtn}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleEnterTask(selectedTask as StudentExamTask, 'exam')}
                >
                  进入考试
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* 学习详情弹窗 */}
      {isLearningModalOpen && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={closeLearningModal}
        >
          <motion.div
            className={styles.learningModal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>{learningDetail?.learning_task_name || '学习任务'}</h2>
              <motion.button
                className={styles.modalClose}
                whileHover={{ rotate: 90 }}
                onClick={closeLearningModal}
              >
                <X size={20} />
              </motion.button>
            </div>

            {learningDetailLoading ? (
              <div className={styles.loadingContent}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                >
                  <Loader2 size={40} className={styles.spinner} />
                </motion.div>
                <p>加载中...</p>
              </div>
            ) : learningDetail ? (
              <>
                <div className={styles.learningInfo}>
                  <div className={styles.learningInfoItem}>
                    <span className={styles.learningInfoLabel}>任务创建者</span>
                    <span className={styles.learningInfoValue}>{learningDetail.creator_name}</span>
                  </div>
                  <div className={styles.learningInfoItem}>
                    <span className={styles.learningInfoLabel}>总体进度</span>
                    <span className={styles.learningInfoValue}>
                      <div className={styles.progressBarSmall}>
                        <div style={{ width: `${learningDetail.progress}%` }} />
                      </div>
                      {learningDetail.progress}%
                    </span>
                  </div>
                  <div className={styles.learningInfoItem}>
                    <span className={styles.learningInfoLabel}>累计学习</span>
                    <span className={styles.learningInfoValue}>{formatDuration(learningDetail.total_learn_duration)}</span>
                  </div>
                </div>

                <div className={styles.fileListHeader}>
                  <h3>学习文件 ({learningDetail.files.length})</h3>
                </div>

                <div className={styles.fileList}>
                  {learningDetail.files.length === 0 ? (
                    <div className={styles.emptyFiles}>
                      <p>暂无学习文件</p>
                    </div>
                  ) : (
                    learningDetail.files.map((file) => (
                      <motion.div
                        key={file.file_id}
                        className={styles.fileItem}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        whileHover={{ x: 5 }}
                      >
                        <div className={styles.fileIcon}>
                          {getFileIcon(file.file_type)}
                        </div>
                        <div className={styles.fileInfo}>
                          <span className={styles.fileName}>{file.file_name}</span>
                          <div className={styles.fileMeta}>
                            <span className={styles.fileArchive}>来自：{file.archive_name}</span>
                            <span>{file.file_type.toUpperCase()}</span>
                            {file.file_size && <span>{formatFileSize(file.file_size)}</span>}
                            <span>学习 {formatDuration(file.learn_duration)}</span>
                            <span className={styles.durationDisplay}>
                              时长要求: {Math.floor((file.required_duration || 60) / 60)} 分钟
                            </span>
                          </div>
                        </div>
                        <div className={styles.fileStatus}>
                          {file.is_completed ? (
                            <span className={styles.completedBadge}>
                              <CheckCircle size={14} />
                              已完成
                            </span>
                          ) : (
                            <span className={styles.pendingBadge}>待学习</span>
                          )}
                        </div>
                        <motion.button
                          className={styles.studyBtn}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => startLearningFile(file)}
                        >
                          <Eye size={16} />
                          {file.is_completed ? '再次学习' : '开始学习'}
                        </motion.button>
                      </motion.div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className={styles.emptyFiles}>
                <p>加载失败</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* 文件预览弹窗 */}
      {previewFile && (
        <motion.div
          className={styles.previewOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={closePreview}
        >
          <motion.div
            className={styles.previewModal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.previewHeader}>
              <h3>{previewFile.file_name}</h3>
              <div className={styles.previewTimer}>
                <Clock size={16} />
                学习中: {formatDuration(fileLearnTimers.get(previewFile.file_id) || 0)}
                <span className={styles.timerSeparator}>|</span>
                需学习{previewFile.required_duration || 60}秒（{Math.floor((previewFile.required_duration || 60) / 60)}分钟）
              </div>
              <motion.button
                className={styles.modalClose}
                whileHover={{ rotate: 90 }}
                onClick={closePreview}
              >
                <X size={20} />
              </motion.button>
            </div>
            <div className={styles.previewContent}>
              {previewFile && previewFile.file_type ? (
                ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'image'].includes(previewFile.file_type.toLowerCase()) ? (
                  <img src={previewFile.preview_url} alt={previewFile.file_name} />
                ) : ['mp4', 'webm', 'ogg', 'video'].includes(previewFile.file_type.toLowerCase()) ? (
                  <div className={styles.videoContainer}>
                    <video
                      ref={videoRef}
                      src={previewFile.preview_url}
                      controls
                      autoPlay
                      muted
                      playsInline
                      preload="auto"
                      onLoadedMetadata={handleVideoLoadedMetadata}
                    />
                    {videoDuration > 0 && (
                      <div className={styles.videoInfo}>
                        视频时长: {formatDuration(videoDuration)} | 累计学习: {formatDuration(fileLearnTimers.get(previewFile.file_id) || previewFile.learn_duration)}
                        {(fileLearnTimers.get(previewFile.file_id) || previewFile.learn_duration) >= videoDuration && (
                          <span className={styles.videoCompleteHint}> ✓ 已达到学习要求</span>
                        )}
                      </div>
                    )}
                  </div>
                ) : ['mp3', 'wav', 'ogg', 'flac', 'audio'].includes(previewFile.file_type.toLowerCase()) ? (
                  <audio src={previewFile.preview_url} controls autoPlay />
                ) : ['pdf'].includes(previewFile.file_type.toLowerCase()) ? (
                  <div className={styles.pdfContainer}>
                    {window.innerWidth <= 768 ? (
                      <PdfViewer url={previewFile.preview_url.replace('/minio/', '/internal-minio/')} />
                    ) : (
                      <object
                        data={previewFile.preview_url.replace('/minio/', '/internal-minio/')}
                        type="application/pdf"
                        className={styles.pdfIframe}
                      >
                        <embed
                          src={previewFile.preview_url.replace('/minio/', '/internal-minio/')}
                          type="application/pdf"
                          className={styles.pdfIframe}
                        />
                      </object>
                    )}
                    <div className={styles.pdfConfirmBar}>
                      <div className={styles.pdfTimerInfo}>
                        阅读时长: {formatDuration(fileLearnTimers.get(previewFile.file_id) || 0)}
                        {(fileLearnTimers.get(previewFile.file_id) || 0) >= (previewFile.required_duration || 60) ? (
                          <span className={styles.pdfReadyHint}> ✓ 已达到学习要求({Math.floor((previewFile.required_duration || 60) / 60)}分钟)</span>
                        ) : (
                          <span className={styles.pdfWaitHint}> 需学习至少 {Math.floor((previewFile.required_duration || 60) / 60)} 分钟</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(previewFile.file_type.toLowerCase()) ? (
                  <iframe src={`/kkfileview/onlinePreview?url=${base64Encode(`${getKKFileViewBaseUrl()}${previewFile.preview_url.replace('/minio/', '/internal-minio/')}`)}`} title={previewFile.file_name} className={styles.officeIframe} frameBorder="0" />
                ) : (
                  <div className={styles.unsupportedPreview}>
                    <File size={64} />
                    <p>此文件类型暂不支持在线预览</p>
                    <a href={previewFile.preview_url} target="_blank" rel="noopener noreferrer" className={styles.downloadLink}>
                      点击下载查看
                    </a>
                    <motion.button
                      onClick={closePreview}
                      className={styles.closeBtn}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      返回
                    </motion.button>
                  </div>
                )
              ) : (
                <div className={styles.unsupportedPreview}>
                  <File size={64} />
                  <p>无法获取文件信息</p>
                  <motion.button
                    onClick={closePreview}
                    className={styles.closeBtn}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    关闭预览
                  </motion.button>
                </div>
              )}
            </div>
            <div className={styles.previewFooter}>
              <motion.button
                className={styles.finishBtn}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={closePreview}
              >
                完成学习
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* 考试页面 */}
      {isExamPageOpen && examTask && user && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, background: 'white' }}>
          <ExamPage
            task={examTask}
            userId={user.user_id}
            onComplete={closeExamPage}
            onCancel={closeExamPage}
          />
        </div>
      )}

      {/* 考试开始确认弹窗 */}
      {isExamConfirmOpen && examConfirmTask && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={cancelStartExam}
        >
          <motion.div
            className={styles.examConfirmModal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>开始考试</h2>
              <motion.button
                className={styles.modalClose}
                whileHover={{ rotate: 90 }}
                onClick={cancelStartExam}
              >
                <X size={20} />
              </motion.button>
            </div>

            <div className={styles.examConfirmContent}>
              <div className={styles.examInfoGrid}>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>任务名称</span>
                  <span className={styles.examInfoValue}>{examConfirmTask.task_name}</span>
                </div>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>试卷名称</span>
                  <span className={styles.examInfoValue}>{examConfirmTask.paper_name || '未命名试卷'}</span>
                </div>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>任务创建者</span>
                  <span className={styles.examInfoValue}>{examConfirmTask.creator_name || '未知'}</span>
                </div>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>考试时长</span>
                  <span className={styles.examInfoValue}>
                    {examConfirmTask.exam_duration ? `${examConfirmTask.exam_duration} 分钟` : '不限时'}
                  </span>
                </div>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>总分</span>
                  <span className={styles.examInfoValue}>{examConfirmTask.total_score} 分</span>
                </div>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>及格分</span>
                  <span className={styles.examInfoValue}>{examConfirmTask.pass_score} 分</span>
                </div>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>题目数量</span>
                  <span className={styles.examInfoValue}>{examConfirmTask.total_questions || 0} 题</span>
                </div>
                <div className={styles.examInfoItem}>
                  <span className={styles.examInfoLabel}>题目类型</span>
                  <span className={styles.examInfoValue}>
                    {examConfirmTask.question_types && Object.keys(examConfirmTask.question_types).length > 0
                      ? Object.entries(examConfirmTask.question_types)
                          .map(([type, count]) => `${getQuestionTypeName(type)} ${count}题`)
                          .join('、')
                      : '未知'}
                  </span>
                </div>
              </div>

              <div className={styles.examWarning}>
                <AlertCircle size={18} />
                <span>进入考试后，必须提交试卷才能退出考试界面。请确保已准备好开始考试。</span>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <motion.button
                className={styles.closeBtn}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={cancelStartExam}
              >
                取消
              </motion.button>
              <motion.button
                className={styles.enterBtn}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={confirmStartExam}
              >
                确认开始
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* 考试结果页面 */}
      {isExamResultOpen && resultTaskId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}>
          <ExamResultPage
            taskId={resultTaskId}
            userId={user!.user_id}
            onBack={closeExamResult}
          />
        </div>
      )}
    </div>
  );
}